/* oxlint-disable typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-return -- generic RFC 7396 merge operates on arbitrary shapes */
import { deepEqual } from 'fast-equals'
import type { DeepPartial } from './types'
import { isObject } from './conversions'

/**
 * Merges a patch object into a target object following the JSON Merge Patch standard (RFC 7396).
 *
 * @param {T} target - The object to be patched.
 * @param {DeepPartial<T>} patch - The patch operations to apply.
 * @param {Opts} [options] - Optional settings for array merging behavior (eg combine instead of override).
 * @returns {T} - The patched object.
 */
type Arrays =
  | 'override'
  | 'combine'
  | ((target: a[], patch: a[], key?: string) => a[])
type Opts = { arrays?: Arrays; undefineds?: 'delete' | 'keep' }
export function merge<T extends Record<string, a>>(
  target: T,
  patch: DeepPartial<T>,
  opts?: Opts,
): T {
  if (typeof patch !== 'object' || patch === null) return patch
  if (typeof target !== 'object' || target === null) target = {} as T

  const arrays = opts?.arrays ?? 'override'
  const undefineds = opts?.undefineds ?? 'delete'

  for (const key of Object.keys(patch)) {
    const patchValue = patch[key] as a
    let targetValue = target[key]

    if (patchValue === undefined) {
      if (undefineds === 'delete') delete target[key]
      else if (undefineds === 'keep') (target as a)[key] = undefined
    } else if (Array.isArray(patchValue)) {
      targetValue = targetValue ?? []
      if (arrays === 'combine') {
        ;(target as a)[key] = [...targetValue, ...patchValue]
      } else if (typeof arrays === 'function') {
        ;(target as a)[key] = arrays(targetValue, patchValue)
      } else {
        ;(target as a)[key] = patchValue // 'override' or no array option provided
      }
    } else if (
      typeof patchValue === 'object' &&
      patchValue !== null &&
      typeof targetValue === 'object' &&
      targetValue !== null &&
      !(patchValue instanceof Date)
    ) {
      target[key as keyof T] = merge(targetValue, patchValue, opts)
    } else {
      target[key as keyof T] = patchValue
    }
  }

  return target
}

/**
 * Derives a JSON Merge Patch (RFC 7396) object that transforms oldData into newData.
 *
 * @param {T} oldData - The original data object.
 * @param {T} newData - The updated data object.
 * @returns {DeepPartial<T>} - The patch object representing the changes.
 */

export function deriveJsonPatch<T extends Record<string, a>>(
  oldData: T | undefined,
  newData: T | undefined,
): DeepPartial<T> {
  if (oldData === newData) return {}

  if (!(oldData && newData)) {
    if (oldData !== newData) return newData as a
    return {}
  }

  const patch: a = {}
  const keys = new Set([...Object.keys(oldData), ...Object.keys(newData)])

  for (const key of keys) {
    const oldVal = oldData[key]
    const newVal = newData[key]

    if (newVal === undefined && oldVal !== undefined) {
      patch[key] = undefined // Key was deleted in newData
    } else if (oldVal === undefined) {
      patch[key] = newVal // Key was added in newData
    } else if (isObject(oldVal) && isObject(newVal)) {
      const subPatch = deriveJsonPatch(oldVal, newVal)
      if (Object.keys(subPatch).length > 0) {
        patch[key] = subPatch
      }
    } else if (Array.isArray(oldVal) && Array.isArray(newVal)) {
      if (!deepEqual(oldVal, newVal)) {
        patch[key] = newVal
      }
    } else if (oldVal !== newVal) {
      patch[key] = newVal
    }
  }

  return patch
}

// oxlint-disable-next-line typescript/no-explicit-any -- aaa
type a = any
